import { Select } from '@mantine/core';
import { ColDef, GridApi, IFilterParams } from 'ag-grid-community';
import { HierarchyAvailableLevelsResponse } from 'api/actions/hierarchy-available-levels/hierarchy-available-levels-response';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import DepartmentLevelCell from 'components/tables/data/department/columns/DepartmentLevelColumn';

export const DEPARTMENT_LEVEL_COLUMN_ID = 'hierarchyLevelSlug';

interface DepartmentLevelFilterModel {
  filter: string;
}

interface DepartmentLevelFloatingFilterProps {
  api: GridApi;
  onChange?: (filters: Record<string, DepartmentLevelFilterModel | null>) => void;
  options: { value: string; label: string; group?: string }[];
}

type Hierarchy = HierarchyAvailableLevelsResponse[number];

/** Returns the exact level slug of the object represented by a row. */
const getRowLevelSlug = (data: Record<string, any>) =>
  data.breadcrumbs?.find(
    ({ departmentId }: { departmentId: number }) => String(departmentId) === String(data.departmentId)
  )?.hierarchyLevelSlug;

/** AG Grid filter matching the selected hierarchy level. */
export const DepartmentLevelColumnFilter = forwardRef(({ filterChangedCallback }: IFilterParams, ref) => {
  const model = useRef<DepartmentLevelFilterModel | null>(null);

  useImperativeHandle(ref, () => ({
    doesFilterPass({ data }: { data: Record<string, any> }) {
      return !model.current?.filter || getRowLevelSlug(data) === model.current.filter;
    },
    getModel: () => model.current,
    setModel: (nextModel: DepartmentLevelFilterModel | null) => {
      model.current = nextModel?.filter ? { filter: String(nextModel.filter) } : null;
    },
    isFilterActive: () => !!model.current?.filter,
    onFloatingFilterChanged(nextModel: DepartmentLevelFilterModel | null) {
      model.current = nextModel?.filter ? { filter: String(nextModel.filter) } : null;
      filterChangedCallback();
    },
  }));

  return null;
});

/** Floating select for the fixed object-level column. */
const DepartmentLevelFloatingFilter = forwardRef(
  ({ api, onChange, options }: DepartmentLevelFloatingFilterProps, ref) => {
    const [value, setValue] = useState<string | null>(() => {
      const filter = api.getFilterModel()[DEPARTMENT_LEVEL_COLUMN_ID]?.filter;
      return filter ? String(filter) : null;
    });

    const pushModel = useCallback(
      (nextValue: string | null) => {
        setValue(nextValue);
        const nextModel = nextValue ? { filter: nextValue } : null;
        if (onChange) {
          onChange({ [DEPARTMENT_LEVEL_COLUMN_ID]: nextModel });
        } else {
          api.setFilterModel({ ...api.getFilterModel(), [DEPARTMENT_LEVEL_COLUMN_ID]: nextModel });
        }
      },
      [api, onChange]
    );

    useImperativeHandle(ref, () => ({
      onParentModelChanged(nextModel: DepartmentLevelFilterModel | null) {
        setValue(nextModel?.filter ? String(nextModel.filter) : null);
      },
    }));

    return (
      <Select
        clearable
        searchable
        size="sm"
        w="100%"
        placeholder="Všetky úrovne"
        data={options}
        value={value}
        onChange={pushModel}
      />
    );
  }
);

/** Creates the fixed level column and profile migration metadata for the object table. */
export default function useDepartmentLevelColumn(permissionSlug?: string) {
  const { getAction } = useApi();
  const [hierarchies, setHierarchies] = useState<Hierarchy[] | null>(null);

  useEffect(() => {
    getAction('HierarchyAvailableLevels')({ query: { filters: { 'permissionSlug.eq': permissionSlug } } })
      .then((availableHierarchies) => setHierarchies(Array.isArray(availableHierarchies) ? availableHierarchies : []))
      .catch(panic);
  }, [getAction, permissionSlug]);

  const orderedHierarchies = useMemo(
    () =>
      [...(hierarchies ?? [])].sort(
        (left, right) =>
          Number(right.isDefault) - Number(left.isDefault) ||
          left.hierarchyName.localeCompare(right.hierarchyName, 'sk') ||
          left.hierarchyId - right.hierarchyId
      ),
    [hierarchies]
  );

  const options = useMemo(
    () =>
      orderedHierarchies.flatMap((hierarchy) =>
        [...hierarchy.levels]
          .sort((left, right) => left.levelOrder - right.levelOrder)
          .map(({ slug, label }) => ({
            value: slug,
            label,
            ...(orderedHierarchies.length > 1 ? { group: hierarchy.hierarchyName } : {}),
          }))
      ),
    [orderedHierarchies]
  );

  const nonLeafLevelSlugs = useMemo(
    () =>
      new Set(
        orderedHierarchies.flatMap(({ levels }) => {
          const leafLevelOrder = Math.max(...levels.map(({ levelOrder }) => levelOrder));
          return levels.filter(({ levelOrder }) => levelOrder < leafLevelOrder).map(({ slug }) => slug);
        })
      ),
    [orderedHierarchies]
  );

  const column = useMemo<ColDef>(
    () => ({
      field: DEPARTMENT_LEVEL_COLUMN_ID,
      headerName: 'Úroveň',
      minWidth: 200,
      sortable: true,
      unSortIcon: true,
      filter: DepartmentLevelColumnFilter,
      floatingFilter: true,
      floatingFilterComponent: forwardRef(function BoundDepartmentLevelFloatingFilter(props, ref) {
        return <DepartmentLevelFloatingFilter {...(props as any)} ref={ref} options={options} />;
      }),
      valueGetter: ({ data }) => data?.levelLabel ?? null,
      cellRenderer: DepartmentLevelCell,
    }),
    [options]
  );

  const profileColumnAliases = useMemo(
    () => ({
      'department.departmentName': 'departmentName',
      ...Object.fromEntries(
        orderedHierarchies.flatMap(({ levels }) => levels.map(({ slug }) => [slug, 'departmentName']))
      ),
    }),
    [orderedHierarchies]
  );

  const resolveProfileFilterColumnId = useCallback((columnId: string, filterModel: any) => {
    const isLegacyNodeFilter =
      columnId === 'departmentName' && filterModel?.filter && !filterModel?.filterType && !filterModel?.type;
    return isLegacyNodeFilter ? '__removedHierarchyNodeFilter' : columnId;
  }, []);

  return {
    column,
    nonLeafLevelSlugs,
    profileColumnAliases,
    profileMigrationReady: hierarchies !== null,
    resolveProfileFilterColumnId,
  };
}
